import "jCrop";
import { _ } from "../polyglot/polyglot.js";
import { extend } from "../helpers/object-util.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import FileSelectionButtonWidget from "./FileSelectionButtonWidget.js";
import jQuery from "jquery";
import OldSpinnerWidget from "../../deprecated/OldSpinnerWidget/OldSpinnerWidget.js"; // eslint-disable-line import-x/no-deprecated -- todo: migrate to new spinner

/**
 * Widget proposing a way to choose a picture,
 * with cropping support.
 *
 * @param {number} spec.height - Output height
 * @param {number} spec.width - Output width
 * @param {boolean} spec.rounded - True if the output is rounded
 * @param {boolean} spec.highResolution - True if the image should be HDPI compatible
 */
export default class ImageChooserWidget extends Widget {
	constructor({ highResolution, height = 0, width = 0, rounded }) {
		super(...arguments);
		this._hdRatio = highResolution ? 2 : 1;
		this._height = height;
		this._width = width;
		this._rounded = rounded;
		this._thumbnailImageContainer = undefined;
		this._jCropApi = undefined;

		// eslint-disable-next-line import-x/no-deprecated -- todo: migrate to new spinner
		this._loadingWidget = new OldSpinnerWidget({
			visible: false,
			size: 32,
			dark: true,
		});

		this._fileSelectionButtonWidget = FileSelectionButtonWidget.forDataURL({
			label: _("Select picture"),
			cssClass: "btn btn-primary",
			fileSelectedCallback: (data) => this._onChange(data),
		});
	}

	getData() {
		return this._thumbnailImageContainer.asJQuery().get(0).src;
	}

	renderContentOn(html) {
		this._container = html.div(
			{
				klass: `image-chooser${this._rounded ? " rounded" : ""}`,
			},
			this._fileSelectionButtonWidget,
			html.div(
				{
					klass: "preview-containers",
				},
				html.div(
					{
						klass: "choosen-container hidden",
					},
					_("Original image"),
					this._loadingWidget,
					(html) => {
						this._chosenImageContainer = html.img();
					},
				),
				html.div(
					{
						klass: "thumbnail-container hidden",
					},
					_("Preview"),
					html.div((html) => {
						this._thumbnailImageContainer = html.img({
							style: `width:${this._width}px;height:${this._height}px`,
						});
					}),
				),
			),
		);
	}

	_onChange(file) {
		let crop;

		const chosenImage = this._chosenImageContainer.asJQuery();

		const parent = this._container.asJQuery();

		this._startLoading();

		this._convertImage(file, parent, (trimmedImage) => {
			chosenImage.get(0).src = "";

			chosenImage.get(0).onload = () => {
				if (this._jCropApi) {
					this._jCropApi.destroy();
				}

				const that = this;

				// eslint-disable-next-line new-cap -- external library we don't control
				chosenImage.Jcrop(
					this._jCropOptions((data) => {
						this.asJQuery().find(".hidden").removeClass("hidden");
						crop = data;
						this._crop(trimmedImage, crop);
					}, this._chosenImageContainer),
					function () {
						that._jCropApi = this;
						that._doneLoading();
					},
				);
			};

			chosenImage.get(0).src = trimmedImage;
		});
	}

	_convertImage(src, parent, fn) {
		const img = new Image();
		const thumbnailContainerLeftMargin = 20; // See image-chooser.less > .thumbnail-container

		img.onload = (event) => {
			const target = event.target;
			const parentWidth =
				parent.width() - this._width - thumbnailContainerLeftMargin;
			const width = Math.min(parentWidth, 800, target.width);
			const height = (width * target.height) / target.width;
			const canvas = jQuery("<canvas/>")
				.attr({
					width,
					height,
				})
				.hide()
				.appendTo("body");
			const ctx = canvas.get(0).getContext("2d");

			ctx.drawImage(
				img,
				0,
				0,
				target.width,
				target.height,
				0,
				0,
				width,
				height,
			);

			const base64ImageData = canvas.get(0).toDataURL();
			canvas.remove();
			fn(base64ImageData);
		};

		img.src = src;
	}

	_crop(src, crop) {
		const img = new Image();

		img.onload = () => {
			const canvas = jQuery("<canvas/>")
				.attr({
					width: this._width * this._hdRatio,
					height: this._height * this._hdRatio,
				})
				.hide()
				.appendTo("body");
			const ctx = canvas.get(0).getContext("2d");

			ctx.drawImage(
				img,
				crop.x,
				crop.y,
				crop.w,
				crop.h,
				0,
				0,
				this._width * this._hdRatio,
				this._height * this._hdRatio,
			);

			const base64ImageData = canvas.get(0).toDataURL();

			this._thumbnailImageContainer.asJQuery().get(0).src =
				base64ImageData;

			canvas.remove();
		};

		img.src = src;
	}

	_jCropOptions(onChange, chosenImageContainer) {
		const options = {
			setSelect: [0, 0, this._width, this._height],
			aspectRatio: this._width / this._height,
			onChange,
		};
		if (this._rounded) {
			options.selectionComponent = this._circleSelection(
				chosenImageContainer.asJQuery(),
			);

			// Only n/s/e/w handles
			options.handles = ["n", "s", "e", "w"];

			// No dragbars or borders
			options.dragbars = [];
			options.borders = [];
		}

		return options;
	}

	_startLoading() {
		this._loadingWidget.show();
	}

	_doneLoading() {
		this._loadingWidget.hide();
	}

	_circleSelection(target) {
		// Create a new Selection object extended from Selection
		function CircleSel() {}

		// Set the custom selection's prototype object to be an instance
		// of the built-in Selection object
		CircleSel.prototype = new jQuery.Jcrop.component.Selection();

		// Then we can continue extending it
		extend(CircleSel.prototype, {
			zoomscale: 1,
			attach: function () {
				this.frame.css({
					background: `url(${target.get(0).src})`,
				});
			},
			positionBg: function (b) {
				this.frame.css({
					backgroundPosition: `${-(b.x + 1)}px ${-b.y - 1}px`,
				});
			},
			redraw: function (b) {
				// Call original update() method first, with arguments
				jQuery.Jcrop.component.Selection.prototype.redraw.call(this, b);

				this.positionBg(this.last);
				return this;
			},
			prototype: jQuery.Jcrop.component.Selection.prototype,
		});

		return CircleSel;
	}
}
